import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { SlackPanel } from '../components/SlackIntegrationGraph';
import { MoreThanAnswers } from './GigaForChatGPT2';
const SURFACES = {
  slack: {
    label: 'Slack',
    channel: '# eng',
    user: 'Priya Nair',
    prompt: '@Giga the signup webhook is throwing 500s since this morning.',
    response: 'Tracing the incident across Slack, code, and deploy history...'
  },
  chatgpt: {
    label: 'ChatGPT',
    channel: 'ChatGPT',
    user: 'Morgan Lee',
    prompt: '@Giga what changed on the onboarding flow today?',
    response: 'Checking recent discussions, pull requests, and product notes...'
  },
  code: {
    label: 'Claude Code + OpenCode',
    channel: 'Claude Code',
    user: 'Alex Chen',
    prompt: 'Use Giga to save this and notify marketing it is fixed.',
    response: 'Saved to Giga. Notified marketing.'
  }
} as const;
export type SurfaceKey = keyof typeof SURFACES;
export function TabIcon({
  slug,
  fallback,
  src
}: {
  slug?: string;
  fallback: string;
  src?: string;
}) {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return <span className="flex h-4 w-4 items-center justify-center rounded bg-[#2B211A] font-title text-[9px] font-extrabold text-white" data-id="element-6523" data-mp="1qvae8zallb2e">
        {fallback}
      </span>;
  }
  return <img src={src || `https://cdn.simpleicons.org/${slug}`} alt="" className="h-4 w-4 object-contain" loading="lazy" onError={() => setFailed(true)} data-id="element-6524" data-mp="1zx9ys1ktmxa3" />;
}
export function CompanyBrain() {
  const [activeSurface, setActiveSurface] = useState<SurfaceKey>('slack');
  const [scrollProgress, setScrollProgress] = useState(0);
  const sectionRef = useRef<HTMLElement | null>(null);
  const isSlack = activeSurface === 'slack';
  useEffect(() => {
    const updateProgress = () => {
      const el = sectionRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const scrollable = Math.max(1, rect.height - window.innerHeight);
      setScrollProgress(Math.min(1, Math.max(0, -rect.top / scrollable)));
    };
    updateProgress();
    window.addEventListener('scroll', updateProgress, {
      passive: true
    });
    window.addEventListener('resize', updateProgress);
    return () => {
      window.removeEventListener('scroll', updateProgress);
      window.removeEventListener('resize', updateProgress);
    };
  }, []);
  const coworkerOpacity = Math.max(0, Math.min(1, 1 - scrollProgress * 2.4));
  const brainOpacity = Math.max(0, Math.min(1, (scrollProgress - 0.36) * 2.4));
  const coworkerY = -24 * scrollProgress;
  const brainY = 24 * (1 - scrollProgress);
  const headlineProgress = Math.max(0, Math.min(1, (scrollProgress - 0.12) / 0.52));
  const coworkerHeadlineOpacity = 1 - headlineProgress;
  const brainHeadlineOpacity = headlineProgress;
  const coworkerHeadlineY = -44 * headlineProgress;
  const brainHeadlineY = 44 * (1 - headlineProgress);
  const coworkerHeadlineRotate = -72 * headlineProgress;
  const brainHeadlineRotate = 72 * (1 - headlineProgress);
  const brainActive = scrollProgress > 0.5;
  return <div className="w-full min-h-screen bg-[#FAF8F4] text-[#2B211A] font-body" data-id="element-6525" data-mp="1ef1mxfidtcqv">
      <section ref={sectionRef} className="relative min-h-[205vh]" data-id="element-6526" data-mp="1ad6k26ksyraz">
        <div className="sticky top-0 mx-auto flex min-h-screen w-full max-w-[1120px] flex-col items-center justify-start px-6 pb-16 pt-24 text-center md:px-10 md:pt-28" data-id="element-6527" data-mp="1adgblylhx6is">
          <div className="relative flex min-h-[205px] w-full flex-col items-center" data-id="element-6528" data-mp="223azrrxmaq5v">
            <RollingTextSlot className="mb-3 h-[1.1rem] font-mono text-[clamp(0.85rem,1vw,1rem)] font-semibold uppercase leading-none tracking-[0.16em] text-[#8A7B70]" progress={headlineProgress} from="Your Team Gets An" to="The Org Gets An" data-id="element-6529" data-mp="1y292hgn9go6j" />
            <RollingTextSlot className="h-[clamp(3.1rem,6.2vw,5.9rem)] font-title text-[clamp(3.4rem,6.6vw,6.25rem)] font-extrabold leading-[0.9] tracking-tight" progress={headlineProgress} from="AI Co-Worker" to="AI Brain" data-id="element-6530" data-mp="1ocpgw385yg7z" />
            <RollingTextSlot className="mt-4 h-[clamp(1.45rem,2.35vw,2.55rem)] font-title text-[clamp(1.35rem,2.25vw,2.45rem)] font-extrabold leading-[1.05] tracking-tight text-[#EA580C]" progress={headlineProgress} from="Available Everywhere, Connected To Everything" to="Company Wide Awareness" data-id="element-6531" data-mp="20m3zwn5zj7bt" />
          </div>

          <div className="relative mt-5 min-h-[610px] w-full md:mt-7" data-id="element-6532" data-mp="1wk8xtgg8cpwz">
            <div className={`absolute inset-x-0 top-0 ${brainActive ? 'pointer-events-none' : ''}`} style={{
            opacity: coworkerOpacity,
            transform: `translateY(${coworkerY}px)`
          }} data-id="element-6533" data-mp="1wmddswrelfdm">
              <CoWorkerExample activeSurface={activeSurface} setActiveSurface={setActiveSurface} isSlack={isSlack} data-id="element-6534" data-mp="2akbcyux5l58j" />
            </div>
            <div className={`absolute inset-x-0 top-0 ${brainActive ? '' : 'pointer-events-none'}`} style={{
            opacity: brainOpacity,
            transform: `translateY(${brainY}px)`
          }} data-id="element-6535" data-mp="2ficwfgyhr6kj">
              <CompanyBrainVisual data-id="element-6536" data-mp="380qu9gjtusjm" />
            </div>
          </div>
        </div>
      </section>
    </div>;
}
function RollingTextSlot({
  from,
  to,
  progress,
  className
}: {
  from: string;
  to: string;
  progress: number;
  className: string;
}) {
  const fromOpacity = 1 - progress;
  const toOpacity = progress;
  const fromY = -28 * progress;
  const toY = 28 * (1 - progress);
  const fromRotate = -68 * progress;
  const toRotate = 68 * (1 - progress);
  return <div className={`relative w-full overflow-visible text-center [perspective:900px] ${className}`} data-id="element-6537" data-mp="2yfdhjtmrpqi5">
      <motion.span className="absolute inset-x-0 top-0 block [transform-style:preserve-3d]" style={{
      opacity: fromOpacity,
      transform: `translateY(${fromY}px) rotateX(${fromRotate}deg)`,
      transformOrigin: 'center bottom'
    }} data-id="element-6538" data-mp="2uejpqp67frjz">
        {from}
      </motion.span>
      <motion.span className="absolute inset-x-0 top-0 block [transform-style:preserve-3d]" style={{
      opacity: toOpacity,
      transform: `translateY(${toY}px) rotateX(${toRotate}deg)`,
      transformOrigin: 'center top'
    }} data-id="element-6539" data-mp="nnllvpp2hbb7">
        {to}
      </motion.span>
    </div>;
}
function CoWorkerExample({
  activeSurface,
  setActiveSurface,
  isSlack
}: {
  activeSurface: SurfaceKey;
  setActiveSurface: (surface: SurfaceKey) => void;
  isSlack: boolean;
}) {
  return <div className="flex w-full flex-col items-center gap-8 text-left" data-id="element-6540" data-mp="zqo92fwqzn06">
      <div className="flex w-full max-w-[760px] flex-wrap items-center justify-center gap-2.5 px-2 md:gap-3" role="tablist" aria-label="Chat surface" data-id="element-6541" data-mp="14nwrmhlt5t1y">
        <SurfaceTab surface="slack" activeSurface={activeSurface} setActiveSurface={setActiveSurface} data-id="element-6542" data-mp="c0ah0pe5xo5l">
          <TabIcon src="https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/slack.svg" fallback="S" data-id="element-6543" data-mp="gzdo0fdpe4g7" />
          Slack
        </SurfaceTab>
        <SurfaceTab surface="code" activeSurface={activeSurface} setActiveSurface={setActiveSurface} ariaLabel="Claude" data-id="element-6544" data-mp="lqvoz3fq8516">
          <TabIcon slug="claude" fallback="C" data-id="element-6545" data-mp="3kfu6refmkofw" />
          Claude
        </SurfaceTab>
        <SurfaceTab surface="chatgpt" activeSurface={activeSurface} setActiveSurface={setActiveSurface} data-id="element-6546" data-mp="3pdm8f062noxa">
          <TabIcon src="https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/openai.svg" fallback="AI" data-id="element-6547" data-mp="3hv60bsdr9niz" />
          ChatGPT
        </SurfaceTab>
      </div>

      <div className="relative w-full max-w-[860px] text-left" data-id="element-6548" data-mp="37wyrjwtmebs2">
        {isSlack ? <div className="relative mx-auto max-w-[760px]" data-id="element-6549" data-mp="32yf6rzqrj61a">
            <div className="absolute -inset-3 rounded-[28px] bg-[#EA580C]/10" aria-hidden="true" data-id="element-6550" data-mp="32xvmicdn45k3" />
            <div className="relative text-left" data-id="element-6551" data-mp="2yyd2rjxk20iu">
              <SlackPanel data-id="element-6552" data-mp="2u6d6q2klvrrj" />
            </div>
          </div> : activeSurface === 'chatgpt' ? <div className="relative mx-auto max-w-[760px]" data-id="element-6553" data-mp="2p70batfo5cdc">
            <div className="absolute -inset-3 rounded-[28px] bg-[#EA580C]/10" aria-hidden="true" data-id="element-6554" data-mp="2p7ig4egecywv" />
            <div className="relative rounded-[24px] bg-white p-3 text-left shadow-[0_28px_80px_rgba(43,33,26,0.13)]" data-id="element-6555" data-mp="2k9gxtvqsp3mg">
              <MoreThanAnswers embedded data-id="element-6556" data-mp="2faxabqwjzjrz" />
            </div>
          </div> : <CodeTerminal data-id="element-6557" data-mp="2b8ai3ovyzwu9" />}
      </div>
    </div>;
}
export function SurfaceTab({
  surface,
  activeSurface,
  setActiveSurface,
  children,
  ariaLabel,
  className = ''
}: {
  surface: SurfaceKey;
  activeSurface: SurfaceKey;
  setActiveSurface: (surface: SurfaceKey) => void;
  children: React.ReactNode;
  ariaLabel?: string;
  className?: string;
}) {
  return <button type="button" role="tab" aria-selected={activeSurface === surface} onClick={() => setActiveSurface(surface)} className={`inline-flex items-center gap-2 rounded-full border px-3.5 py-2 text-sm font-semibold shadow-sm backdrop-blur-sm transition-all ${activeSurface === surface ? 'border-[#EA580C]/30 bg-[#FFF4EC] text-[#2B211A] ring-2 ring-[#EA580C]/20' : 'border-neutral-200/70 bg-white/80 text-[#2B211A] hover:border-[#EA580C]/30'} ${className}`} aria-label={ariaLabel} data-id="element-6558" data-mp="3u4uc27o0azw7">
      {children}
    </button>;
}
export function CodeTerminal() {
  return <>
      <div className="absolute -inset-3 rounded-[28px] bg-[#EA580C]/10" aria-hidden="true" data-id="element-6559" data-mp="2xfd5lqh799tg" />
      <div className="relative overflow-hidden rounded-[24px] border border-[#2B211A]/20 bg-[#17120E] p-3 text-left shadow-[0_28px_80px_rgba(43,33,26,0.22)]" data-id="element-6560" data-mp="31fndj8ojyaay">
        <div className="rounded-[18px] border border-white/10 bg-[#201913]" data-id="element-6561" data-mp="2dtbe6ph7yzdm">
          <div className="flex items-center gap-2 border-b border-white/10 px-4 py-3" data-id="element-6562" data-mp="2rsjdrjhao9ic">
            <span className="h-3 w-3 rounded-full bg-[#FF5F57]" data-id="element-6563" data-mp="2rq6o0k5aouuq" />
            <span className="h-3 w-3 rounded-full bg-[#FFBD2E]" data-id="element-6564" data-mp="2mrwqw400fk6h" />
            <span className="h-3 w-3 rounded-full bg-[#28C840]" data-id="element-6565" data-mp="31pxhcii4d3t8" />
            <span className="ml-3 font-mono text-xs font-medium text-white/45" data-id="element-6566" data-mp="31oe1gt0dkurz">claude-code ~/giga</span>
          </div>

          <div className="min-h-[390px] px-5 py-5 font-mono text-[13px] leading-relaxed text-[#F5EFE7] md:text-sm" data-id="element-6567" data-mp="3aismlc7l6zj0">
            <div className="space-y-4" data-id="element-6568" data-mp="36h75tfrxm13j">
              <div className="flex items-start gap-3" data-id="element-6569" data-mp="36i8q8auu7zxx">
                <span className="text-[#EA580C]" data-id="element-6570" data-mp="3kgf9akv4nvdj">$</span>
                <p className="text-white" data-id="element-6571" data-mp="3kevpwle6d47j">claude</p>
              </div>
              <p className="pl-6 text-[#F7C9AC]" data-id="element-6572" data-mp="3fjoue0v275tp">
                The checkout campaign page is returning a 500 after the auth update. Find the cause and patch it.
              </p>
              <p className="pl-6 text-[#CFC5BA]" data-id="element-6573" data-mp="1lsv5sx074drm">
                Reading the failing route, auth middleware, and checkout tests...
              </p>
              <p className="pl-6 text-[#CFC5BA]" data-id="element-6574" data-mp="1hpyod9me6np9">
                Found it. The campaign preview still expects the old session field. Updating the helper and rerunning the tests.
              </p>
              <p className="pl-6 text-[#E9DED2]" data-id="element-6575" data-mp="29h635q6onvb5">Fixed. Tests pass.</p>
            </div>

            <div className="mt-8 flex items-start gap-3" data-id="element-6576" data-mp="25fsul8c7he0j">
              <span className="text-[#EA580C]" data-id="element-6577" data-mp="20hrg70pq62ve">$</span>
              <p className="text-white" data-id="element-6578" data-mp="1vjpx4fsau3jx">Use Giga to save this and notify marketing it is fixed.</p>
            </div>

            <p className="mt-4 pl-6 text-[#CFC5BA]" data-id="element-6579" data-mp="p46s2cvijco0">Saved to Giga. Notified marketing.</p>
          </div>
        </div>
      </div>
    </>;
}
function CompanyBrainVisual() {
  const [activeBrainTab, setActiveBrainTab] = useState('organize');
  return <div className="mx-auto w-full max-w-[820px]" data-id="element-6580" data-mp="a272mwwa66yl">
      <div className="mb-5 flex flex-wrap items-center justify-center gap-2.5" role="tablist" aria-label="Brain view" data-id="element-6581" data-mp="ezgvkmivp0r6">
        {['Organize', 'Understand', 'Answer'].map(tab => <button key={tab} type="button" role="tab" aria-selected={activeBrainTab === tab.toLowerCase()} onClick={() => setActiveBrainTab(tab.toLowerCase())} className={`rounded-full border px-3.5 py-2 text-sm font-semibold shadow-sm backdrop-blur-sm transition-all ${activeBrainTab === tab.toLowerCase() ? 'border-[#EA580C]/30 bg-[#FFF4EC] text-[#2B211A] ring-2 ring-[#EA580C]/20' : 'border-neutral-200/70 bg-white/80 text-[#2B211A] hover:border-[#EA580C]/30'}`} data-id="element-6582" data-mp="jwqnc8tqpoi6">
            {tab}
          </button>)}
      </div>

      <div className="relative" data-id="element-6583" data-mp="34go2sz7jhet8">
        <div className="absolute -inset-3 rounded-[28px] bg-[#EA580C]/10" aria-hidden="true" data-id="element-6584" data-mp="34hfuhyy0mse8" />
        <div className="relative overflow-hidden rounded-[26px] border border-[#D8CEC3] bg-white p-4 shadow-[0_28px_80px_rgba(43,33,26,0.13)] md:p-5" data-id="element-6585" data-mp="39ezd1b1qxrbz">

          <div className="relative min-h-[520px] overflow-hidden rounded-[22px] border border-[#EFE7DE] bg-[#FAF8F4]" data-id="element-6586" data-mp="31h5tixknu288">
            {activeBrainTab === 'organize' ? <OrganizeBrainTree data-id="element-6587" data-mp="3beaeiu2rrrng" /> : <BrainPlaceholder title={activeBrainTab === 'understand' ? 'Understand every moving part' : 'Ask across the org'} data-id="element-6588" data-mp="3betxme45om32" />}
          </div>
        </div>
      </div>
    </div>;
}
function OrgNode({
  title,
  meta,
  className = ''
}: {
  title: string;
  meta: string;
  className?: string;
}) {
  return <div className={`absolute z-10 w-[210px] rounded-2xl border border-[#E3DDD5] bg-white px-4 py-3 shadow-[0_4px_16px_rgba(43,33,26,0.08)] ${className}`} data-id="element-6589" data-mp="3fekk45mngg1s">
      <p className="truncate font-title text-[15px] font-extrabold leading-tight text-[#2B211A]" data-id="element-6590" data-mp="3p447nolb4y2z">{title}</p>
      <p className="mt-1 truncate text-[13px] font-medium text-[#8A7B70]" data-id="element-6591" data-mp="6y97qv9kisdc">{meta}</p>
    </div>;
}
function OrganizeBrainTree() {
  return <div className="relative h-[520px] min-w-[820px] origin-top-left scale-[0.74] sm:scale-[0.82] lg:scale-[0.88]" data-id="element-6592" data-mp="2za91sefjruc">
      <svg className="absolute inset-0 h-full w-full" viewBox="0 0 820 520" fill="none" aria-hidden="true" data-id="element-6593" data-mp="ljn8jbp8enzh">
        <path d="M410 52V95M160 95H660M160 95V145M660 95V145" stroke="#8CCB9B" strokeWidth="3" data-id="element-6594" data-mp="qighcqtkpqtb" />
        <path d="M160 226V255M60 255H350M60 255V305M350 255V305" stroke="#9BD0DF" strokeWidth="3" data-id="element-6595" data-mp="cne2vfanut4w" />
        <path d="M660 226V255M560 255H760M760 255V305" stroke="#9BD0DF" strokeWidth="3" data-id="element-6596" data-mp="hlz3vgxqlgue" />
        <path d="M60 386V455M60 455H290M290 455V482" stroke="#8CCB9B" strokeWidth="3" data-id="element-6597" data-mp="2t5ijevsa6ht" />
        <path d="M760 386V430M760 430H610M610 430V482" stroke="#B795FF" strokeWidth="3" data-id="element-6598" data-mp="7n9qa0cviijy" />
      </svg>

      <div className="absolute left-1/2 top-7 z-20 flex h-12 w-12 -translate-x-1/2 items-center justify-center rounded-full bg-[#2B211A] text-white shadow-[0_8px_20px_rgba(43,33,26,0.18)]" data-id="element-6599" data-mp="3pxv1nb9ek2s3">
        <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/giga-mark.png" alt="" className="h-6 w-6 rounded" data-id="element-6600" data-mp="3g2jw9jjvw7x0" />
      </div>

      <OrgNode title="Company Brain" meta="Giga · shared context" className="left-[55px] top-[145px]" data-id="element-6601" data-mp="raznk2tghh25" />
      <OrgNode title="Revenue Org" meta="Sales · CS · finance" className="left-[555px] top-[145px]" data-id="element-6602" data-mp="n9nrpfjznzli" />
      <OrgNode title="Engineering" meta="Repos · incidents · deploys" className="left-[10px] top-[305px]" data-id="element-6603" data-mp="ie8gkvdd8hss" />
      <OrgNode title="Growth" meta="Campaigns · ads · analytics" className="left-[300px] top-[305px]" data-id="element-6604" data-mp="8idu1p60u2kq" />
      <OrgNode title="Customer Support" meta="Tickets · renewals · risks" className="left-[610px] top-[305px]" data-id="element-6605" data-mp="3jkdtozcnxgv" />
      <OrgNode title="Auth Migration" meta="Project · PRs · decisions" className="left-[155px] top-[482px]" data-id="element-6606" data-mp="3vmlbg9e7ksfp" />
      <OrgNode title="Launch Campaign" meta="Meta Ads · website · Slack" className="left-[475px] top-[482px]" data-id="element-6607" data-mp="3qos6fjon64h0" />
    </div>;
}
function BrainPlaceholder({
  title
}: {
  title: string;
}) {
  return <div className="flex min-h-[520px] items-center justify-center p-8 text-center" data-id="element-6608" data-mp="1ow3veo5racqo">
      <div data-id="element-6609" data-mp="1wgfw2qfhyfw6">
        <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.16em] text-[#8A7B70]" data-id="element-6610" data-mp="1wgo8yibmdl7d">Coming next</p>
        <h3 className="mt-3 font-title text-4xl font-extrabold tracking-tight text-[#2B211A]" data-id="element-6611" data-mp="21e7qps7j0g42">{title}</h3>
      </div>
    </div>;
}